<template>
  <page-container
    :loading-footer-button="loadingFooterButton"
    :title="calendarTitle"
    @primary-cta-click="goToNextPage"
    @back-cta-click="goToBackPage"
  >
    <template #content>
      <form
        class="pb-8"
        @submit.prevent="goToNextPage"
      >
        <pp-form-message
          v-if="areUserThePatient.showError"
          ref="commonError"
          class="form-common-error px-3 pt-2 pb-4 mt-7 mb-n4"
          type="error"
          data-cy-id="form-are-you-the-patient-error"
          :text="cantScheduleErrorText"
        />
        <pp-input-checkbox
          v-if="showNewPatientField"
          v-model="formData.newPatient"
          hide-details="true"
          label="I'm a new patient at this practice"
          class="mt-6"
          data-cy-id="form-new-patient"
          :disabled="calendarUseScreeningQuestions"
          small
          @click="trackEvent({
            action: 'form_input',
            label: 'new-patient-toggle'
          })"
        />
        <template
          v-if="showReasonField"
        >
          <component
            :is="reasonField"
            v-model="formData.reason"
            :items="reasonOptions"
            :aria-required="isRequired('reason') ? 'true' : 'false'"
            :error-messages="getErrorMessages('reason')"
            class="mt-5"
            data-cy-id="form-reason"
            label="Reason for visit"
            no-data-text="No Values"
            @blur="blurField($v.formData.reason, 'reason')"
          />
        </template>
        <pp-input-select
          v-if="showProviderField"
          v-model="formData.doctorId"
          :items="providerOptions"
          :aria-required="showProviderField ? 'true' : 'false'"
          :error-messages="getErrorMessages('doctorId')"
          class="mt-5"
          data-cy-id="form-provider"
          label="Provider"
          no-data-text="No Values"
          @blur="blurField($v.formData.doctorId, 'doctorId')"
        />
        <pp-input-select
          v-if="showLocationField"
          v-model="formData.locationId"
          :aria-required="showLocationField ? 'true' : 'false'"
          :items="locationOptions"
          :error-messages="getErrorMessages('locationId')"
          class="mt-5"
          data-cy-id="form-location"
          label="Location"
          no-data-text="No Values"
          @blur="blurField($v.formData.locationId, 'locationId')"
        />
        <p
          v-if="selectedAppointmentTypeFeeInDollars"
          data-cy-id="payment-message-step-b"
          class="mb-0 mt-6"
        >
          {{ appointmentWithFeeMessage }}
        </p>
        <pp-row class="my-0">
          <pp-col
            class="py-0"
            cols="12"
            sm="6"
          >
            <pp-input-text
              v-if="showField('firstName')"
              v-model="formData.firstName"
              :error-messages="getErrorMessages('firstName')"
              :aria-required="isRequired('firstName') ? 'true' : 'false'"
              label="First Name"
              data-cy-id="form-firstname"
              class="mt-5"
              @blur="blurField($v.formData.firstName, 'first-name')"
            />
          </pp-col>
          <pp-col
            class="py-0"
            cols="12"
            sm="6"
          >
            <pp-input-text
              v-if="showField('lastName')"
              v-model="formData.lastName"
              :error-messages="getErrorMessages('lastName')"
              :aria-required="isRequired('lastName') ? 'true' : 'false'"
              label="Last Name"
              data-cy-id="form-lastname"
              class="mt-5"
              @blur="blurField($v.formData.lastName, 'last-name')"
            />
          </pp-col>
        </pp-row>
        <pp-row class="my-0">
          <pp-col
            class="py-0"
            cols="12"
            sm="6"
          >
            <pp-input-text
              v-if="showField('email')"
              v-model="formData.email"
              :error-messages="getErrorMessages('email')"
              label="Email"
              :aria-required="isRequired('email') ? 'true' : 'false'"
              type="email"
              data-cy-id="form-email"
              class="mt-5"
              @blur="blurField($v.formData.email, 'email')"
              @focus="scrollToView($event)"
            />
          </pp-col>
          <pp-col
            class="py-0"
            cols="12"
            sm="6"
          >
            <pp-input-text
              v-if="showField('phone')"
              v-model="formData.phone"
              v-mask="'(###) ### ####'"
              :error-messages="getErrorMessages('phone')"
              :aria-required="isRequired('phone') ? 'true' : 'false'"
              label="Phone"
              type="tel"
              data-cy-id="form-phone"
              class="mt-5"
              @blur="blurField($v.formData.phone, 'phone')"
              @focus="scrollToView($event)"
            />
          </pp-col>
        </pp-row>
        <pp-input-text
          v-if="showField('dateOfBirth')"
          v-model="formData.dateOfBirth"
          v-mask="'##/##/####'"
          :error-messages="getErrorMessages('dateOfBirth')"
          :aria-required="isRequired('dateOfBirth') ? 'true' : 'false'"
          label="Date of Birth"
          placeholder="mm/dd/yyyy"
          type="tel"
          data-cy-id="form-date-of-birth"
          class="mt-5 data-form__date-of-birth-field"
          @blur="blurField($v.formData.dateOfBirth, 'date-of-birth')"
          @focus="scrollToView($event)"
        />
        <component
          :is="isMobile ? 'pp-input-toggle-group': 'pp-input-radio-group'"
          v-if="areUserThePatient.showField"
          v-model="formData.areUserThePatient"
          :options="areUserThePatient.options"
          :error-messages="getErrorMessages('areUserThePatient')"
          class="mt-7"
          :mandatory="false"
          :label="`Are you the patient?`"
          name="are-you-the-patient"
          row
          data-cy-id="form-are-you-the-patient"
          @blur="blurField($v.formData.areUserThePatient, 'are-user-the-patient')"
          @focus="scrollToView($event)"
        />
        <pp-input-radio-group
          v-if="showField('sex')"
          v-model="formData.sex"
          :options="sexOptions"
          :aria-required="`${isRequired('sex') ? 'true' : 'false'}`"
          :error-messages="getErrorMessages('sex')"
          class="mt-5"
          :label="`Sex ${isRequired('sex') ? '' : ' (optional)'}`"
          name="sex"
          row
          data-cy-id="form-sex"
          @blur="blurField($v.formData.sex, 'sex')"
          @focus="scrollToView($event)"
        />
        <pp-input-checkbox
          v-model="formData.agreePrivacyPolicy"
          :disabled="privacyCheckboxDisabled"
          :error-messages="getErrorMessages('agreePrivacyPolicy')"
          aria-required="true"
          class="privacy-policy-checkbox mt-5"
          data-cy-id="agree-privacy-policy"
          small
          @blur="$v.formData.agreePrivacyPolicy.$touch()"
          @focus="scrollToView($event)"
          @click="trackEvent({
            action: 'form_input',
            label: 'privacy-policy'
          })"
        >
          <template #label>
            I have read and agreed to the <a
              :href="`${$originHost}/your-privacy`"
              target="_blank"
              class="blue--text"
              @click="trackEvent({
                action: 'link',
                label: 'privacy'
              })"
            >Privacy Policy</a> and <a
              :href="`${$originHost}/our-terms`"
              target="_blank"
              class="blue--text"
              @click="trackEvent({
                action: 'link',
                label: 'terms'
              })"
            >Terms of Use</a> that I am at least 13 and have the authority to make this appointment
          </template>
        </pp-input-checkbox>
        <pp-input-checkbox
          v-model="formData.agreeSmsTerms"
          :disabled="loadingFooterButton"
          aria-required="true"
          data-cy-id="agree-sms-terms"
          label="I agree to receive text messages from this practice and understand that message frequency and data rates may apply"
          class="mt-4"
          small
          :error-messages="getErrorMessages('agreeSmsTerms')"
          @focus="scrollToView($event)"
          @click="trackEvent({
            action: 'form_input',
            label: 'agree-sms'
          })"
        />
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="footerText"
          class="mt-4 grey--text text--darken-4"
          v-html="footerText"
        />
      </form>
    </template>
  </page-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Calendar from '@/entities/Calendar'
import PageContainer from '@/components/page-container/PageContainer'
import { VueMaskDirective } from 'v-mask'
import { validationMixin } from 'vuelidate'
import { FieldErrorMessagesMixin } from '@/mixins/fieldErrorMessagesMixin'
import formAnalytics from '@/mixins/formAnalytics'
import { email, minLength, requiredIf, sameAs } from 'vuelidate/lib/validators'
import {
  VISIT_TYPE_IN_PERSON_VALUE,
  VISIT_TYPE_VIRTUAL_VISIT_VALUE,
  REFERRER_BACK_BUTTON,
  CALENDAR_TYPES,
  APPT_FEE_PARAGRAPH,
  MIN_AGE_REQUIRED
} from '@/views/constants'
import { datadogRum } from '@datadog/browser-rum'
import { validAppointmentTypeByPatientType, validLegacyReasonType, createListForInputSelect, alphaWithQuotes } from '@/utils/helpers'
import * as uiTypes from '@/store/ui/mutations-types'

export default {
  name: 'ViewStepB',
  components: {
    'page-container': PageContainer
  },
  directives: { mask: VueMaskDirective },
  mixins: [validationMixin, FieldErrorMessagesMixin, formAnalytics],
  data () {
    return {
      isMobile: this.$ppUi.isMobile,
      loadingFooterButton: false,
      sexOptions: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
        { label: 'Other', value: 'other' }
      ],
      visitTypeOptions: [
        { label: 'In-person', value: VISIT_TYPE_IN_PERSON_VALUE },
        { label: 'Virtual visit', value: VISIT_TYPE_VIRTUAL_VISIT_VALUE }
      ],
      areUserThePatient: {
        showField: false,
        showError: false,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ]
      },
      apptFeeParagraph: APPT_FEE_PARAGRAPH
    }
  },
  computed: {
    ...mapState({
      appointmentTypesEnabled: state => state.legacy.appointmentTypesEnabled,
      filterRowShown: state => state.ui.filterRowShown,
      calendars: state => state.legacy.calendars
    }),
    ...mapGetters({
      formData: 'formData/formData',
      reasons: 'legacy/reasons',
      filters: 'ui/filters',
      calendarFooterText: 'legacy/calendarFooterText',
      calendarTitle: 'legacy/calendarTitle',
      getFormatedApptPhoneForCalendar: 'legacy/getFormatedApptPhoneForCalendar',
      someCalendarsWithScreeningQuestions: 'legacy/someCalendarsWithScreeningQuestions',
      appointmentTypeComplexityExist: 'legacy/appointmentTypeComplexityExist',
      getPracticeGroup: 'legacy/getPracticeGroup',
      screeningSettings: 'legacy/screeningSettings',
      configuredAppointmentTypesByScheduleIdsList: 'legacy/configuredAppointmentTypesByScheduleIdsList',
      telehealthPractice: 'legacy/telehealthPractice',
      filterIsFilled: 'ui/filterIsFilled',
      filledSteps: 'ui/filledSteps',
      patientTypeFlagName: 'formData/patientTypeFlagName',
      selectedAppointmentTypeFeeInDollars: 'formData/selectedAppointmentTypeFeeInDollars'
    }),
    calendar () {
      return this.calendars[this.formData.calendarIndex] || new Calendar()
    },
    cantScheduleErrorText () {
      const phone = this.getFormatedApptPhoneForCalendar(this.formData.calendarIndex)
      return `We unfortunately can’t schedule this type of appointment online. Please call our office ${phone ? 'at ' + phone : ''} to make your appointment.`
    },
    privacyCheckboxDisabled () {
      if (this.loadingFooterButton) return true
      return !this.validAgeForAppointmentCreation(new Date(this.formData.dateOfBirth)) && this.formData.areUserThePatient
    },
    locationOptions () {
      return createListForInputSelect(this.calendar.displayInfo.displayLocations.list, 'internalName')
    },
    providerOptions () {
      return createListForInputSelect(this.calendar.displayInfo.displayProviders.list)
    },
    legacyReasonsOptions () {
      const options = []
      const reasons = this.reasons(this.formData.calendarIndex)
      if (!reasons) {
        return options
      }
      reasons.forEach((reason) => {
        let showOptionInList = validLegacyReasonType(reason.reasonType, {
          newPatientFilter: this.formData.newPatient
        })
        const validLocationId = this.formData.locationId && reason.locationId ? +this.formData.locationId === +reason.locationId : true
        const validProviderId = this.formData.doctorId && reason.doctorId ? +this.formData.doctorId === +reason.doctorId : true
        showOptionInList = showOptionInList && validLocationId && validProviderId

        if (showOptionInList) {
          options.push({ text: reason.name, value: reason.id })
        }
      })
      return options
    },
    apptTypesForCurCalendar () {
      return this.configuredAppointmentTypesByScheduleIdsList([this.formData.scheduleId])
    },
    appointmentTypesOptions () {
      const options = []
      this.apptTypesForCurCalendar.forEach((apptType) => {
        const showInList = validAppointmentTypeByPatientType(apptType.configurations, {
          newPatientFilter: this.formData.newPatient
        })
        if (showInList) {
          options.push({ text: apptType.displayName, value: apptType.appointmentTypeId })
        }
      })
      return options
    },
    reasonOptions () {
      return this.appointmentTypesEnabled ? this.appointmentTypesOptions : this.legacyReasonsOptions
    },
    reasonField () {
      return this.reasonOptions.length === 0 ? 'PpInputText' : 'PpInputSelect'
    },
    showLocationField () {
      const validCalendarType = [CALENDAR_TYPES.PRACTICE_TYPE, CALENDAR_TYPES.PROVIDER_TYPE].includes(this.calendar.type)
      const hideBecauseItsSelectedOnStepA = this.filterRowShown && this.filterIsFilled('locationIdFilter')
      return !hideBecauseItsSelectedOnStepA && validCalendarType && this.calendar.displayInfo.displayLocations.list.length > 1 && !this.telehealthPractice
    },
    showProviderField () {
      const validCalendarType = [CALENDAR_TYPES.PRACTICE_TYPE, CALENDAR_TYPES.LOCATION_TYPE].includes(this.calendar.type)
      const hideBecauseItsSelectedOnStepA = this.filterRowShown && this.filterIsFilled('providerIdFilter')
      return !hideBecauseItsSelectedOnStepA && !this.calendar.settings.skipProviderSelection && validCalendarType &&
        this.calendar.displayInfo.displayProviders.list.length > 1
    },
    footerText () {
      return this.calendarFooterText(this.formData.calendarIndex)
    },
    calendarUseScreeningQuestions () {
      const settings = this.screeningSettings(this.formData.calendarIndex)
      return Boolean(settings) && Boolean(settings !== '' || settings.length)
    },
    showNewPatientField () {
      return !this.filterRowShown
    },
    showReasonField () {
      return this.showField('reason') && !this.filterIsFilled('appointmentReasonFilter')
    },
    appointmentWithFeeMessage () {
      return `${this.apptFeeParagraph} $${this.selectedAppointmentTypeFeeInDollars}.`
    }
  },
  watch: {
    reasonField (newVal, oldVal) {
      if (newVal !== oldVal) {
        // clear reason on reason component type change
        this.formData.reason = ''
      }
    },
    privacyCheckboxDisabled (isDisabled) {
      if (isDisabled && !this.loadingFooterButton) {
        this.$set(this.formData, 'agreePrivacyPolicy', false)
      }
    }
  },
  created () {
    if (!this.filledSteps.stepA) {
      datadogRum.addAction('stepB opened by directUrl', {
        message: 'stepB opened, but stepA was not filled',
        formData: {
          newPatient: this.formData.newPatient,
          isTelehealth: this.formData.isTelehealth,
          reason: this.formData.reason,
          insuranceProviderId: this.formData.insuranceProviderId,
          doctorId: this.formData.doctorId,
          locationId: this.formData.locationId,
          timeBlock: this.formData.timeBlock,
          calendarIndex: this.formData.calendarIndex,
          calendarId: this.formData.calendarId,
          scheduleId: this.formData.scheduleId,
          calendarBlockId: this.formData.calendarBlockId
        },
        practice: {
          id: this.$practiceId,
          step: 'B'
        },
        redirectedTo: 'stepA'
      })
      this.$router.replace({ name: 'stepA', query: this.$route.query || {} })
    }
  },
  methods: {
    toggleInvalidPatientAgeError (state = true) {
      const showError = typeof state === 'boolean' ? state : !this.areUserThePatient.showError

      this.areUserThePatient.showError = showError
      showError && (this.$set(this.formData, 'agreePrivacyPolicy', false))
      this.$nextTick(() => {
        this.scrollToCommonErrorMessage()
      })
    },
    scrollToCommonErrorMessage () {
      this.$refs.commonError && (this.scrollToView({ target: this.$refs.commonError.$el }))
    },
    validAgeForAppointmentCreation (dateOfBirth) {
      if (isNaN(dateOfBirth)) {
        return false
      }
      const curDate = new Date(Date.now())
      const minStartingDate = new Date(curDate.getFullYear() - MIN_AGE_REQUIRED, curDate.getMonth(), curDate.getDate())
      return minStartingDate > dateOfBirth
    },
    validDobDate (date) {
      if (date && date.length !== 10) {
        return false
      }
      const validAge = this.validAgeForAppointmentCreation(new Date(date))
      this.areUserThePatient.showField = !validAge
      return !isNaN(new Date(date)) && new Date(date) < new Date()
    },
    goToNextPage () {
      this.loadingFooterButton = true
      this.submitAnalytics('step-b-continue')
      const showInvalidAgeError = !this.validAgeForAppointmentCreation(new Date(this.formData.dateOfBirth)) && !!this.formData.areUserThePatient
      this.toggleInvalidPatientAgeError(showInvalidAgeError)

      if (this.$v.$invalid) {
        this.$v.$touch()
        this.loadingFooterButton = false
        return
      }
      if (showInvalidAgeError) {
        this.loadingFooterButton = false
        return
      }
      if (this.appointmentTypesEnabled && this.reasonOptions.length > 0) {
        this.formData.appointmentTypeUuid = this.formData.reason
      }
      return this.$store.dispatch('formData/setFormData', {
        ...this.formData,
        origin: this.$widgetOrigin
      }).then(() => {
        this.$store.commit(`ui/${uiTypes.SET_STEP_FILLED}`, 'stepB')
        if (this.areRequiredFieldsOnStepC) {
          this.$router.replace({ name: 'stepC', query: this.$route.query || {} })
        } else {
          this.$store.dispatch('legacy/saveAppointment', this.formData)
            .then(() => {
              this.trackEvent({ action: `book_${this.patientTypeFlagName}_patient` })
              this.trackEvent({ action: 'book_complete' })
              datadogRum.addAction('confirmed', {
                practice: {
                  id: this.$practiceId,
                  step: 'B'
                }
              })
              // logging for appointments from calendar with otherProvider availability
              if (this.formData.doctorId !== this.formData.showedDoctorId && this.calendar.settings.communication.showAsOtherCalendar) {
                datadogRum.addAction('otherProvider calendar appointment', {
                  practice: {
                    id: this.$practiceId,
                    step: 'B',
                    doctorId: this.formData.doctorId,
                    showedDoctorId: this.formData.showedDoctorId
                  }
                })
              }
              this.$router.replace({ name: 'stepC', query: this.$route.query || {} })
            })
            .catch((e) => {
              this.trackEvent({
                action: 'book_error_step_b',
                label: e.message
              })
              e.name !== 'SaveAppointmentError' && console.error(e)
              this.$router.replace({ name: 'stepA', params: { error: e }, query: this.$route.query || {} })
            })
        }
      })
    },
    goToBackPage () {
      this.$router.replace({ name: 'stepA', params: { referrer: REFERRER_BACK_BUTTON }, query: this.$route.query || {} })
    }
  },
  validations () {
    return {
      formData: {
        firstName: {
          required: requiredIf(() => this.isRequired('firstName')),
          alphaWithQuotes,
          minLength: minLength(2)
        },
        lastName: {
          required: requiredIf(() => this.isRequired('lastName')),
          alphaWithQuotes,
          minLength: minLength(2)
        },
        email: {
          required: requiredIf(() => this.isRequired('email')),
          email
        },
        phone: {
          required: requiredIf(() => this.isRequired('phone')),
          validPhone: (v) => this.validPhone(v, !this.isRequired('phone'))
        },
        sex: {
          required: requiredIf(() => this.isRequired('sex'))
        },
        dateOfBirth: {
          required: requiredIf(() => this.isRequired('dateOfBirth')),
          validDob: (v) => this.validDobDate(v)
        },
        reason: {
          required: requiredIf(() => this.isRequired('reason'))
        },
        doctorId: {
          required: requiredIf(() => this.showProviderField)
        },
        locationId: {
          required: requiredIf(() => this.showLocationField)
        },
        isTelehealth: {
          required: requiredIf(() => this.isRequired('visitType'))
        },
        agreePrivacyPolicy: {
          required: sameAs(() => true)
        },
        agreeSmsTerms: {
          required: sameAs(() => true)
        },
        areUserThePatient: {
          required: requiredIf(() => !this.validAgeForAppointmentCreation(new Date(this.formData.dateOfBirth)))
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.privacy-policy-checkbox {
  ::v-deep {
    label {
      display: block !important; /* stylelint-disable-line declaration-no-important */
    }
  }
}

.data-form {
  &__date-of-birth-field {
    @include desktop() {
      max-width: 145px;
    }
  }
}
</style>
